import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import * as pdfjs from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker

export function landlordTermsViewUrl(
	blobUrl: string,
	opts?: { disposition?: 'inline' | 'attachment' },
) {
	// Demo / local public assets and browser object URLs skip the authenticated
	// Azure blob proxy — they are already fetchable by the browser.
	if (blobUrl.startsWith('/') || blobUrl.startsWith('blob:')) {
		return blobUrl
	}
	const params = new URLSearchParams({ url: blobUrl })
	if (opts?.disposition === 'attachment') {
		params.set('disposition', 'attachment')
	}
	return `/resources/view-blob?${params.toString()}`
}

function isPdfTerms(fileName?: string | null, blobUrl?: string) {
	const haystack = `${fileName ?? ''} ${blobUrl ?? ''}`.toLowerCase()
	return haystack.includes('.pdf') || haystack.includes('application/pdf')
}

/**
 * Renders landlord terms on-screen inside the consent modal.
 * PDFs are drawn to canvas (no browser PDF toolbar).
 */
export function LandlordTermsDocumentViewer({
	blobUrl,
	fileName,
}: {
	blobUrl: string
	fileName?: string | null
}) {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(true)

	const sourceUrl = landlordTermsViewUrl(blobUrl)
	const pdf = isPdfTerms(fileName, blobUrl)

	useEffect(() => {
		if (!pdf) {
			setLoading(false)
			return
		}

		let cancelled = false
		const container = containerRef.current
		if (!container) return

		container.replaceChildren()
		setLoading(true)
		setError(null)

		void (async () => {
			try {
				const task = pdfjs.getDocument({ url: sourceUrl })
				const doc = await task.promise
				if (cancelled) {
					await doc.destroy()
					return
				}

				for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
					if (cancelled) break
					const page = await doc.getPage(pageNum)
					const baseViewport = page.getViewport({ scale: 1 })
					const width = Math.max(container.clientWidth - 8, 320)
					const scale = Math.min(width / baseViewport.width, 1.75)
					const viewport = page.getViewport({ scale })

					const canvas = document.createElement('canvas')
					canvas.width = viewport.width
					canvas.height = viewport.height
					canvas.className = 'mb-4 w-full bg-white last:mb-0'
					canvas.setAttribute('aria-label', `Page ${pageNum}`)

					const context = canvas.getContext('2d')
					if (!context) continue
					await page.render({
						canvasContext: context,
						viewport,
					}).promise
					if (!cancelled) container.appendChild(canvas)
				}

				await doc.destroy()
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error ? err.message : 'Could not load document',
					)
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()

		return () => {
			cancelled = true
			container.replaceChildren()
		}
	}, [pdf, sourceUrl])

	if (!pdf) {
		return (
			<div className="relative overflow-y-auto bg-white px-4 py-2">
				<img
					src={sourceUrl}
					alt="Landlord Terms & Conditions"
					className="mx-auto w-auto max-w-full"
				/>
			</div>
		)
	}

	return (
		<div className="relative bg-white">
			{loading ? (
				<div className="flex min-h-[120px] flex-col items-center justify-center gap-3 bg-white py-8">
					<Loader2 className="size-9 animate-spin text-[#32302F]" />
					<span className="text-sm font-medium text-gray-500">
						Loading document…
					</span>
				</div>
			) : null}
			{error ? (
				<p className="shrink-0 px-6 py-4 text-sm text-[#be123d]">{error}</p>
			) : null}
			<div ref={containerRef} className="px-4 py-2" />
		</div>
	)
}
